import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Skeleton, Tooltip } from '@mui/material';
import MyLotsIcon from '@mui/icons-material/DashboardOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import CheckIcon from '@mui/icons-material/Check';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import logoTerrioBlanco from '../../assets/Logo-Terrio-blanco.png';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { colors, layout } from '../../../styles/theme';
import { useStaffSession } from '../../../Context/StaffSessionContext';
import MapHeaderSearchFilters from './MapHeaderSearchFilters';
import { HEADER_WITH_SEARCH_GRID_COLUMNS } from './MapHeaderSearchFilters.styles';
import MapHeaderLoginButton from './MapHeaderLoginButton';
import { useActiveSpace } from '../../context/ActiveSpaceContext';
import SpaceMembersDialog from '../SpaceMembersDialog/SpaceMembersDialog';
import { getSpaceOwnerLabel } from '../../../services/spaceMembersService';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade';
import { toast } from '../../../Components/Toast';

// ─── Breakpoint ────────────────────────────────────────────────────────────────
const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth < MOBILE_BREAKPOINT
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

// ─── Tokens ────────────────────────────────────────────────────────────────────
const HEADER_HEIGHT = layout.mapHeaderHeight;
const SIDEBAR_RAIL_WIDTH = layout.sidebarRailWidth;
const SIDEBAR_ITEM_HEIGHT = layout.sidebarItemHeight;
const SIDEBAR_ITEM_GAP = 8;
const SIDEBAR_ICON_SIZE = layout.sidebarIconSize;
const SIDEBAR_TOP_INSET = layout.mapPanelTopInset;
const RAIL_ACTIVE_BG = colors.backgroundDark;
const RAIL_ACTIVE_ICON_COLOR = colors.primary;
const MOBILE_DRAWER_WIDTH = 220;

function getUserDisplayInfo(user) {
  const userEmail = user?.email || '';
  const displayName = user?.name || (userEmail ? userEmail.split('@')[0] : 'Mi cuenta');
  const initial = (user?.name || userEmail || 'M').trim().charAt(0).toUpperCase();
  return { displayName, userEmail, initial };
}

const menuDividerStyle = {
  height: '1px',
  backgroundColor: 'rgba(255,255,255,0.06)',
  margin: '4px 6px',
};

const menuActionStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '9px 12px',
  border: 'none',
  borderRadius: '8px',
  backgroundColor: 'transparent',
  color: 'rgba(255,255,255,0.88)',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 500,
  textAlign: 'left',
  textDecoration: 'none',
  transition: 'background-color 0.15s ease, color 0.15s ease',
};

const menuLogoutStyle = {
  ...menuActionStyle,
  padding: '8px 12px',
  color: 'rgba(255,255,255,0.55)',
  fontSize: '12px',
  fontWeight: 400,
};

function AccountMenuPanel({
  displayName,
  userEmail,
  initial,
  onOpenBranding,
  onOpenFeedback,
  onRestartTour,
  onLogout,
  onOpenAdmin,
  onClose,
}) {
  const handleBranding = () => {
    onClose?.();
    onOpenBranding?.();
  };

  const handleFeedback = () => {
    onClose?.();
    onOpenFeedback?.();
  };

  const handleRestartTour = () => {
    onClose?.();
    onRestartTour?.();
  };

  const handleLogout = () => {
    onClose?.();
    onLogout?.();
  };

  const handleOpenAdmin = () => {
    onClose?.();
    onOpenAdmin?.();
  };

  const applyHover = (e, active) => {
    e.currentTarget.style.backgroundColor = active ? 'rgba(255,255,255,0.06)' : 'transparent';
  };

  return (
    <>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 12px 10px',
      }}>
        <span style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#2c7ef8',
          color: '#ffffff',
          fontSize: '14px',
          fontWeight: 700,
          textTransform: 'uppercase',
          flexShrink: 0,
        }}>
          {initial}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {displayName}
          </div>
          {userEmail && (
            <div style={{
              color: 'rgba(255,255,255,0.48)',
              fontSize: '12px',
              fontWeight: 400,
              marginTop: '2px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {userEmail}
            </div>
          )}
        </div>
      </div>

      <div style={menuDividerStyle} />

      {onOpenBranding && (
        <button
          type="button"
          onClick={handleBranding}
          style={menuActionStyle}
          onMouseEnter={(e) => applyHover(e, true)}
          onMouseLeave={(e) => applyHover(e, false)}
        >
          <PaletteOutlinedIcon sx={{ fontSize: 17, color: 'rgba(255,255,255,0.72)' }} />
          <span>Identidad</span>
        </button>
      )}

      {onOpenFeedback && (
        <button
          type="button"
          onClick={handleFeedback}
          style={menuActionStyle}
          onMouseEnter={(e) => applyHover(e, true)}
          onMouseLeave={(e) => applyHover(e, false)}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 17, color: 'rgba(255,255,255,0.72)' }} />
          <span>Enviar feedback</span>
        </button>
      )}

      {onRestartTour && (
        <button
          type="button"
          onClick={handleRestartTour}
          style={menuActionStyle}
          onMouseEnter={(e) => applyHover(e, true)}
          onMouseLeave={(e) => applyHover(e, false)}
        >
          <ExploreOutlinedIcon sx={{ fontSize: 17, color: 'rgba(255,255,255,0.72)' }} />
          <span>Ver tour rápido</span>
        </button>
      )}

      {onOpenAdmin && (
        <>
          <div style={menuDividerStyle} />
          <button
            type="button"
            onClick={handleOpenAdmin}
            style={menuActionStyle}
            onMouseEnter={(e) => applyHover(e, true)}
            onMouseLeave={(e) => applyHover(e, false)}
          >
            <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 17, color: 'rgba(255,255,255,0.72)' }} />
            <span>Administración</span>
          </button>
        </>
      )}

      <div style={menuDividerStyle} />

      <button
        type="button"
        onClick={handleLogout}
        style={menuLogoutStyle}
        onMouseEnter={(e) => applyHover(e, true)}
        onMouseLeave={(e) => applyHover(e, false)}
      >
        <LogoutIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)' }} />
        <span>Cerrar sesión</span>
      </button>
    </>
  );
}

// ─── Desktop styles ─────────────────────────────────────────────
const headerStyle = {
  position: 'absolute',
  top: 0, left: 0, right: 0,
  height: `${HEADER_HEIGHT}px`,
  backgroundColor: colors.backgroundSurface,
  borderBottom: `1px solid ${colors.border}`,
  display: 'grid',
  gridTemplateColumns: HEADER_WITH_SEARCH_GRID_COLUMNS,
  alignItems: 'center',
  padding: '0 10px',
  zIndex: 1100,
  boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
  gap: '12px',
};

const headerLogoCellStyle = {
  display: 'flex',
  alignItems: 'center',
  justifySelf: 'start',
};

const headerAuthCellStyle = {
  display: 'flex',
  alignItems: 'center',
  justifySelf: 'end',
};

const iconGroupStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

const authButtonStyle = {
  width: '40px', height: '40px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 0,
  borderRadius: '999px',
  border: '1px solid rgba(255,255,255,0.12)',
  backgroundColor: 'rgba(255,255,255,0.06)',
  color: '#ffffff',
  marginRight: '12px',
  cursor: 'pointer',
};

const avatarStyle = {
  width: '28px', height: '28px',
  borderRadius: '50%',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  backgroundColor: '#2c7ef8',
  color: '#ffffff',
  fontSize: '13px', fontWeight: 700,
  textTransform: 'uppercase',
  flexShrink: 0,
};

const menuStyle = {
  position: 'absolute',
  top: 'calc(100% + 28px)', right: 0,
  minWidth: '260px',
  backgroundColor: 'rgba(24,24,24,0.96)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: '14px',
  boxShadow: '0 16px 40px rgba(0,0,0,0.38)',
  padding: '6px',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  animation: 'accountMenuIn 0.18s ease',
};

const railTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: 'rgba(17,17,17,0.92)',
      border: '1px solid rgba(255,255,255,0.10)',
      boxShadow: '0 14px 35px rgba(0,0,0,0.45)',
      borderRadius: '10px',
      px: 1.25, py: 0.75,
      fontSize: '12px', fontWeight: 600, letterSpacing: '0.15px',
      backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
    },
  },
  arrow: {
    sx: {
      color: 'rgba(17,17,17,0.92)',
      '&:before': { border: '1px solid rgba(255,255,255,0.10)' },
    },
  },
};

const sidebarRailStyle = {
  position: 'absolute',
  top: `${HEADER_HEIGHT}px`, left: 0, bottom: 0,
  width: `${SIDEBAR_RAIL_WIDTH}px`,
  backgroundColor: 'rgba(20,20,20,0.72)',
  borderRight: '1px solid rgba(255,255,255,0.10)',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  zIndex: 1090,
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  // El pie lo ocupa el botón de feedback (mismo alto que un ítem del rail).
  padding: `${SIDEBAR_TOP_INSET}px 0 ${12 + SIDEBAR_ITEM_HEIGHT + SIDEBAR_ITEM_GAP}px`, gap: `${SIDEBAR_ITEM_GAP}px`,
  boxSizing: 'border-box',
};

const railIconSvgStyle = {
  fontSize: SIDEBAR_ICON_SIZE,
  width: SIDEBAR_ICON_SIZE,
  height: SIDEBAR_ICON_SIZE,
  display: 'block',
};

const railIconButtonStyle = {
  width: `${SIDEBAR_ITEM_HEIGHT}px`, height: `${SIDEBAR_ITEM_HEIGHT}px`,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  backgroundColor: 'transparent', border: 'none',
  color: '#f5f5f5', opacity: 0.9,
  borderRadius: '12px', cursor: 'pointer', outline: 'none',
  transform: 'translateY(0px)', boxShadow: 'none',
  transition: 'background-color 0.18s ease, opacity 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease',
};

// ─── Mobile styles ─────────────────────────────────────────────────────────────

const mobileHeaderStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0,
  height: `${HEADER_HEIGHT}px`,
  backgroundColor: 'rgb(28,28,28)',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0 12px',
  zIndex: 1100,
  boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
};

const hamburgerButtonStyle = {
  width: '36px', height: '36px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'none', border: 'none',
  color: '#ffffff', cursor: 'pointer',
  borderRadius: '10px', padding: 0, flexShrink: 0,
  WebkitTapHighlightColor: 'transparent',
};

// ─── Desktop rail button ───────────────────────────────────────────────────────

function RailButton({ isActive, onClick, disabled, children, tooltip, dataTour }) {
  return (
    <Tooltip title={tooltip} placement="right" arrow slotProps={railTooltipSlotProps}>
      <button
        type="button"
        data-tour={dataTour}
        onClick={onClick}
        aria-pressed={!!isActive}
        disabled={disabled}
        style={{
          ...railIconButtonStyle,
          backgroundColor: isActive ? RAIL_ACTIVE_BG : 'transparent',
          color: isActive ? RAIL_ACTIVE_ICON_COLOR : '#f5f5f5',
          opacity: disabled ? 0.45 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        onMouseEnter={(e) => {
          if (disabled) return;
          e.currentTarget.style.backgroundColor = isActive
            ? RAIL_ACTIVE_BG
            : 'rgba(255,255,255,0.06)';
          e.currentTarget.style.opacity = 1;
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = isActive ? 'none' : '0 10px 24px rgba(0,0,0,0.35)';
        }}
        onMouseLeave={(e) => {
          if (disabled) return;
          e.currentTarget.style.backgroundColor = isActive ? RAIL_ACTIVE_BG : 'transparent';
          e.currentTarget.style.color = isActive ? RAIL_ACTIVE_ICON_COLOR : '#f5f5f5';
          e.currentTarget.style.opacity = disabled ? 0.45 : 1;
          e.currentTarget.style.transform = 'translateY(0px)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {children}
      </button>
    </Tooltip>
  );
}

// ─── Nav items config ──────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { key: 'myLots',    label: 'Registros',   icon: MyLotsIcon,            toggleKey: 'onToggleMyLots',    openKey: 'isMyLotsOpen',    tourKey: 'mis-lotes' },
  { key: 'lists',     label: 'Mis listas',  icon: ListAltOutlinedIcon,   toggleKey: 'onToggleLists',     openKey: 'isListsOpen',     tourKey: 'mis-listas' },
  // Leads: solo si MapPage pasa onToggleLeads (docs/maps/leads.md).
  { key: 'leads',     label: 'Leads',       icon: PersonAddOutlinedIcon, toggleKey: 'onToggleLeads',     openKey: 'isLeadsOpen',     tourKey: 'leads' },
  { key: 'history',   label: 'Historial',   icon: HistoryOutlinedIcon,   toggleKey: 'onToggleHistory',   openKey: 'isHistoryOpen',   tourKey: 'historial' },
  // Campañas: solo si MapPage pasa onToggleMetaAds (flag + getmetaconnection; docs/maps/meta-ads.md).
  // Después de Historial, no al pie: ahí está el botón de feedback.
  { key: 'metaAds',   label: 'Campañas',    icon: CampaignOutlinedIcon,  toggleKey: 'onToggleMetaAds',   openKey: 'isMetaAdsOpen',   tourKey: 'meta-ads' },
];

// ─── Mobile drawer ─────────────────────────────────────────────────────────────

function MobileDrawer({
  isOpen,
  onClose,
  navProps,
  sharedSpaceLabel = null,
  onExitSharedSpace = null,
  hideHistory = false,
}) {
  const touchStartX = useRef(null);
  const navItems = NAV_ITEMS
    .filter(({ key }) => !(hideHistory && key === 'history'))
    .filter(({ key }) => key !== 'leads' || Boolean(navProps.onToggleLeads))
    .filter(({ key }) => key !== 'metaAds' || Boolean(navProps.onToggleMetaAds));

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    if (dx > 48) onClose();
    touchStartX.current = null;
  };

  return (
    <>
      {/* Overlay */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 1090,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
        }}
      />

      {/* Drawer */}
      <nav
        aria-label="Menú principal"
        aria-hidden={!isOpen}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          position: 'fixed',
          top: `${HEADER_HEIGHT}px`, left: 0, bottom: 0,
          width: `${MOBILE_DRAWER_WIDTH}px`,
          backgroundColor: 'rgba(18,18,18,0.97)',
          borderRight: '1px solid rgba(255,255,255,0.09)',
          backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
          zIndex: 1098,
          display: 'flex', flexDirection: 'column',
          padding: '10px 8px', gap: '4px',
          transform: isOpen ? 'translateX(0)' : `translateX(-${MOBILE_DRAWER_WIDTH + 4}px)`,
          transition: 'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)',
          boxShadow: isOpen ? '4px 0 24px rgba(0,0,0,0.45)' : 'none',
        }}
      >
        {sharedSpaceLabel && onExitSharedSpace ? (
          <div style={{
            margin: '4px 4px 10px',
            padding: '10px 12px',
            borderRadius: '12px',
            border: '1px solid rgba(72, 146, 145, 0.45)',
            backgroundColor: 'rgba(72, 146, 145, 0.18)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...spaceMenuKickerStyle, color: colors.primary }}>
                Invitado
              </div>
              <div style={{ ...spaceMenuNameStyle, marginTop: '2px' }}>
                {sharedSpaceLabel}
              </div>
              <div style={{
                marginTop: '4px',
                color: 'rgba(255,255,255,0.55)',
                fontSize: '12px',
                lineHeight: 1.35,
              }}>
                Los lotes y listas que ves son de este espacio.
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                onExitSharedSpace();
                onClose();
              }}
              style={{
                border: 'none',
                borderRadius: '8px',
                padding: '8px 10px',
                backgroundColor: colors.primary,
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Volver a mi espacio
            </button>
          </div>
        ) : null}

        {navItems.map(({ key, label, icon: Icon, toggleKey, openKey, tourKey }) => {
          const isActive = !!navProps[openKey];
          const onToggle = navProps[toggleKey];
          const disabled = !onToggle;

          return (
              <button
                key={key}
                type="button"
                data-tour={tourKey}
                onClick={() => {
                  if (disabled) return;
                  onToggle();
                  onClose();
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '11px 14px',
                  borderRadius: '10px', border: 'none',
                  backgroundColor: isActive ? RAIL_ACTIVE_BG : 'transparent',
                  color: disabled
                    ? 'rgba(255,255,255,0.30)'
                    : isActive
                      ? RAIL_ACTIVE_ICON_COLOR
                      : 'rgba(255,255,255,0.78)',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: isActive ? 600 : 400,
                  textAlign: 'left', width: '100%',
                  transition: 'background-color 0.15s ease, color 0.15s ease',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onTouchStart={(e) => {
                  if (!disabled) e.currentTarget.style.backgroundColor =
                    isActive ? RAIL_ACTIVE_BG : 'rgba(255,255,255,0.06)';
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.backgroundColor =
                    isActive ? RAIL_ACTIVE_BG : 'transparent';
                }}
              >
                <span style={{
                  display: 'flex',
                  opacity: disabled ? 0.4 : 1,
                  color: isActive ? RAIL_ACTIVE_ICON_COLOR : 'inherit',
                }}>
                  <Icon fontSize="small" />
                </span>
                <span style={{ flex: 1 }}>{label}</span>
                {isActive && (
                  <span style={{
                    width: '6px', height: '6px',
                    borderRadius: '50%',
                    backgroundColor: RAIL_ACTIVE_ICON_COLOR,
                    flexShrink: 0,
                  }} />
                )}
              </button>
          );
        })}
      </nav>
    </>
  );
}

// ─── Bottom sheet usuario (mobile) ────────────────────────────────────────────

function UserBottomSheet({
  isOpen,
  onClose,
  displayName,
  userEmail,
  initial,
  onOpenBranding,
  onOpenFeedback,
  onRestartTour,
  onLogout,
  onOpenAdmin,
}) {
  if (!isOpen) return null;
  return (
    <div
      role="dialog" aria-modal="true" aria-label="Menú de cuenta"
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.55)',
        zIndex: 1200,
        display: 'flex', alignItems: 'flex-end',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%',
        backgroundColor: 'rgba(24,24,24,0.98)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '20px 20px 0 0',
        padding: '12px 10px',
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}>
        <div style={{
          width: '36px', height: '4px', borderRadius: '99px',
          backgroundColor: 'rgba(255,255,255,0.18)',
          margin: '0 auto 16px',
        }} />

        <div style={{ position: 'relative' }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.45)',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              zIndex: 1,
            }}
            aria-label="Cerrar"
          >
            <CloseIcon fontSize="small" />
          </button>

          <AccountMenuPanel
            displayName={displayName}
            userEmail={userEmail}
            initial={initial}
            onOpenBranding={onOpenBranding}
            onOpenFeedback={onOpenFeedback}
            onRestartTour={onRestartTour}
            onLogout={onLogout}
            onOpenAdmin={onOpenAdmin}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  );
}


// ─── Chrome espacio (primary Terrio, no infoBlue) ─────────────────────────────

/** Acento verde primario — mismos tokens que rail activo / modales. */
const SPACE_ACCENT = colors.primary;
const SPACE_ACCENT_BORDER = 'rgba(72, 146, 145, 0.45)';
const SPACE_ACCENT_BG = 'rgba(72, 146, 145, 0.20)';
const SPACE_ACCENT_SOFT = 'rgba(72, 146, 145, 0.14)';
const SPACE_ACCENT_RING = 'rgba(72, 146, 145, 0.28)';

/** Cinco filas visibles, con aire entre cada una; el resto scrollea con fade. */
const SHARED_SPACES_LIST_MAX_HEIGHT = 220;

const sharedSpacesSectionLabelStyle = {
  padding: '8px 12px 4px',
  color: 'rgba(255,255,255,0.45)',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const sharedSpaceNameStyle = {
  minWidth: 0,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const sharedSpaceCurrentStyle = {
  flexShrink: 0,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.02em',
  color: SPACE_ACCENT,
};

const spaceMenuKickerStyle = {
  margin: 0,
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const spaceMenuNameStyle = {
  margin: '2px 0 0',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: '18px',
  color: '#E8F4F4',
};

const sharedSpacesStatusStyle = {
  margin: 0,
  padding: '2px 12px 10px',
  color: 'rgba(255,255,255,0.45)',
  fontSize: '12px',
  lineHeight: '16px',
};

/**
 * Lista de espacios ajenos. El activo se marca, no se atenúa.
 * Si no hay ninguno, el hueco queda con el mismo alto que la carga
 * para que el menú no se achique al terminar el GET.
 * Pieza del chip SpaceHeaderControl (no del menú de cuenta).
 */
function SharedSpacesMenuSection({ sharedSpaces, activeOwnerUserId, onSelect, loading = false }) {
  const listRef = useRef(null);
  const { scrollRef, fadeTop, fadeBottom, refreshScrollEdges } = useScrollEdgeFade();
  const spaces = useMemo(
    () => (Array.isArray(sharedSpaces) ? sharedSpaces : []),
    [sharedSpaces],
  );

  const setListNode = useCallback((node) => {
    listRef.current = node;
    scrollRef(node);
  }, [scrollRef]);

  useEffect(() => {
    const list = listRef.current;
    const current = list?.querySelector('[aria-current="true"]');
    if (list && current) {
      const top = current.offsetTop;
      const bottom = top + current.offsetHeight;
      if (top < list.scrollTop) list.scrollTop = top;
      else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight;
      }
    }
    refreshScrollEdges();
  }, [spaces, activeOwnerUserId, refreshScrollEdges]);

  const showList = spaces.length > 0;

  return (
    <div>
      <div style={menuDividerStyle} />
      <div style={sharedSpacesSectionLabelStyle}>Espacios compartidos</div>
      {loading && !showList ? (
        <Box aria-busy="true" sx={{ px: '12px', pt: '2px', pb: '10px' }}>
          <Skeleton
            variant="rounded"
            width="78%"
            height={16}
            aria-hidden
            sx={{ bgcolor: '#383838', borderRadius: '4px' }}
          />
        </Box>
      ) : null}
      {!loading && !showList ? (
        <p style={sharedSpacesStatusStyle}>No tenés espacios compartidos.</p>
      ) : null}
      {showList ? (
      <Box
        ref={setListNode}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          maxHeight: SHARED_SPACES_LIST_MAX_HEIGHT,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { width: 0, height: 0, display: 'none' },
          ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
        }}
      >
        {spaces.map((space) => {
          const ownerId = String(space?.owner_user_id || '');
          const spaceLabel = getSpaceOwnerLabel(space);
          const isActive = Boolean(activeOwnerUserId) && ownerId === String(activeOwnerUserId);
          return (
            <button
              key={ownerId || spaceLabel}
              type="button"
              role="menuitem"
              aria-current={isActive ? 'true' : undefined}
              onClick={() => {
                if (isActive) return;
                onSelect(space);
              }}
              style={{
                ...menuActionStyle,
                color: isActive ? '#E8F4F4' : 'rgba(255,255,255,0.88)',
                backgroundColor: isActive ? SPACE_ACCENT_SOFT : 'transparent',
                cursor: isActive ? 'default' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isActive ? SPACE_ACCENT_SOFT : 'transparent';
              }}
            >
              {isActive
                ? <CheckIcon sx={{ fontSize: 17, color: SPACE_ACCENT }} />
                : <LoginOutlinedIcon sx={{ fontSize: 17, color: 'rgba(255,255,255,0.72)' }} />}
              <span style={sharedSpaceNameStyle}>{spaceLabel}</span>
              {isActive ? <span style={sharedSpaceCurrentStyle}>Actual</span> : null}
            </button>
          );
        })}
      </Box>
      ) : null}
    </div>
  );
}

const sharedSpaceChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  maxWidth: '280px',
  height: '40px',
  minHeight: '40px',
  padding: '4px 6px 4px 10px',
  borderRadius: '999px',
  border: `1px solid ${SPACE_ACCENT_BORDER}`,
  backgroundColor: SPACE_ACCENT_BG,
  color: '#E8F4F4',
  boxSizing: 'border-box',
};

/**
 * Chrome fijo de espacio en el header.
 * - Propio: el chip dice “Mi espacio”. El desplegable muestra el nombre
 *   asignado (o “Mi Espacio”) y ofrece configurar.
 * - Ajeno: mismo chip con el label del espacio activo y volver al propio.
 */
function SpaceHeaderControl({
  compact = false,
  label,
  mySpaceLabel = 'Mi Espacio',
  isViewingSharedSpace = false,
  sharedSpaces = [],
  activeOwnerUserId = null,
  loadingSharedSpaces = false,
  onOpenConfig,
  onEnterSpace,
  onExitToOwnSpace,
  onMenuOpen,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const chipText = isViewingSharedSpace ? (label || 'Espacio compartido') : 'Mi espacio';

  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const openMenu = () => {
    toast.clear();
    onMenuOpen?.();
    setOpen(true);
  };

  const handleChipClick = () => {
    if (open) setOpen(false);
    else openMenu();
  };

  const chipStyle = {
    ...sharedSpaceChipStyle,
    ...(compact ? { maxWidth: '168px', minHeight: '30px', padding: '3px 4px 3px 8px', gap: '6px' } : {}),
    ...(isViewingSharedSpace ? {} : {
      border: '1px solid rgba(255,255,255,0.16)',
      backgroundColor: 'rgba(255,255,255,0.06)',
    }),
    cursor: 'pointer',
    position: 'relative',
  };

  const itemStyle = {
    ...menuActionStyle,
    width: '100%',
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', flexShrink: 1, minWidth: 0 }}>
      <Tooltip
        title={isViewingSharedSpace ? `Invitado en ${label}` : 'Tu espacio'}
        placement="bottom"
        arrow
      >
        <span style={{ display: 'inline-flex', maxWidth: '100%', minWidth: 0 }}>
          <button
            type="button"
            data-tour="espacios"
            onClick={handleChipClick}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={isViewingSharedSpace ? `Invitado en ${label}` : chipText}
            style={{
              ...chipStyle,
              backgroundImage: 'none',
              font: 'inherit',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: compact ? 16 : 17, color: SPACE_ACCENT, flexShrink: 0 }} />
            <span style={{
              minWidth: 0,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: compact ? '11px' : '12px',
              fontWeight: 700,
              letterSpacing: '0.01em',
              color: '#E8F4F4',
            }}>
              {chipText}
            </span>
            {isViewingSharedSpace && !compact ? (
              <span style={{
                flexShrink: 0,
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: SPACE_ACCENT,
              }}>
                Invitado
              </span>
            ) : null}
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 18,
                color: 'rgba(255,255,255,0.65)',
                flexShrink: 0,
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s ease',
              }}
            />
          </button>
        </span>
      </Tooltip>

      {open && (
        <div
          role="menu"
          style={{
            ...menuStyle,
            top: 'calc(100% + 8px)',
            right: 0,
            left: 'auto',
            minWidth: isViewingSharedSpace ? 280 : (compact ? 220 : 260),
            zIndex: 1200,
          }}
        >
          {isViewingSharedSpace ? (
            <div style={{
              margin: '2px 2px 6px',
              padding: '10px 12px',
              borderRadius: '10px',
              border: `1px solid ${SPACE_ACCENT_BORDER}`,
              backgroundColor: SPACE_ACCENT_SOFT,
            }}>
              <div style={{ ...spaceMenuKickerStyle, color: SPACE_ACCENT }}>Invitado</div>
              <div style={spaceMenuNameStyle}>{label}</div>
              <div style={{
                marginTop: '4px',
                color: 'rgba(255,255,255,0.62)',
                fontSize: '12px',
                lineHeight: 1.35,
              }}>
                Los lotes y listas que ves son de este espacio.
              </div>
            </div>
          ) : (
            <div style={{ padding: '8px 12px 2px' }}>
              <div style={{ ...spaceMenuKickerStyle, color: 'rgba(255,255,255,0.45)' }}>Tu espacio</div>
              <div style={spaceMenuNameStyle}>{label || 'Mi Espacio'}</div>
            </div>
          )}

          {!isViewingSharedSpace ? (
            <button
              type="button"
              role="menuitem"
              style={itemStyle}
              onClick={() => {
                setOpen(false);
                onOpenConfig?.();
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <PeopleOutlineIcon sx={{ fontSize: 17, color: 'rgba(255,255,255,0.72)' }} />
              <span>Configurar mi espacio</span>
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              style={itemStyle}
              onClick={() => {
                setOpen(false);
                onExitToOwnSpace?.();
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <HomeOutlinedIcon sx={{ fontSize: 17, color: SPACE_ACCENT }} />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Volver a mi espacio
              </span>
            </button>
          )}

          <SharedSpacesMenuSection
            sharedSpaces={sharedSpaces}
            activeOwnerUserId={activeOwnerUserId}
            loading={loadingSharedSpaces}
            onSelect={(space) => {
              setOpen(false);
              onEnterSpace?.(space);
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── MapHeader ─────────────────────────────────────────────────────────────────

function MapHeader({
  isLoggedIn,
  user,
  onLoginClick,
  onLogoutClick,
  onOpenBranding,
  onOpenFeedback,
  onRestartTour,
  navDrawerOpen,
  onNavDrawerOpenChange,
  onUserMenuOpenChange,
  onToggleMyLots, isMyLotsOpen,
  onToggleLists, isListsOpen,
  onToggleHistory, isHistoryOpen,
  onToggleLeads, isLeadsOpen = false,
  onToggleMetaAds, isMetaAdsOpen = false,
  showSearchControl = false,
  showFilterControl = false,
  showListingFilters = false,
  showOfficialUnitFilters = false,
  showOfficialUnitCoverageNote = false,
  officialUnitFilter = null,
  officialUnitZoomLocked = false,
  lotViewQuotaExceeded = false,
  lotViewQuotaResetsAt = null,
  activeFilters,
  activeFilterCount = 0,
  showOnlyFiltered,
  onSetFilterValue,
  onRemoveFilterChip,
  onToggleShowOnlyFiltered,
  onClearAllFilters,
  onOpenFilterResultsList = null,
  isFilterResultsOpen = false,
  filterScope,
  filterScopeLists,
  filterScopeListsLoading,
  filterScopeLoading,
  filterScopeError,
  filterScopeCount,
  filterScopeMatchCount,
  onSetFilterScope,
  onFilterScopeDropdownOpen,
  filterScopeLocked = false,
  filterScopeLockedHint = null,
  mapFilterOptions,
  onRequestMapFilterOptions,
  onFlyTo,
  parseSearchEnabled = false,
  parseSearchContext = null,
  onApplyParsedLotFilters = null,
  onLotViewQuotaInfo = null,
  resolveViewProvince = null,
  // Modo lista CAD: otro contrato de filtros, no el de parcelas catastrales.
  filterGroups = null,
  resolveAppliedChips = null,
  hideFilterTabs = false,
  hideShowOnlyFiltered = false,
  skipPlaceSearch = false,
}) {
  const { displayName, userEmail, initial } = getUserDisplayInfo(user);
  const navigate = useNavigate();
  const { isStaff } = useStaffSession();
  const {
    isViewingSharedSpace,
    activeSpaceLabel,
    headerSpaceLabel,
    mySpaceHeaderLabel,
    activeOwnerUserId,
    exitToOwnSpace,
    enterSpace,
    sharedSpaces,
    loadingSharedSpaces,
    refreshSharedSpaces,
    applyMySpaceProfile,
  } = useActiveSpace();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [spaceMembersOpen, setSpaceMembersOpen] = useState(false);
  const [internalDrawerOpen, setInternalDrawerOpen] = useState(false);
  const isDrawerControlled = onNavDrawerOpenChange != null;
  const isDrawerOpen = isDrawerControlled ? Boolean(navDrawerOpen) : internalDrawerOpen;
  const setIsDrawerOpen = (next) => {
    const resolved = typeof next === 'function' ? next(isDrawerOpen) : next;
    if (isDrawerControlled) onNavDrawerOpenChange(resolved);
    else setInternalDrawerOpen(resolved);
  };
  const menuRef = useRef(null);
  const location = useLocation();
  const isMobile = useIsMobile();

  const authPages = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/verify'];
  const isAuthPage = authPages.includes(location.pathname);

  // Cerrar dropdown desktop al click fuera
  useEffect(() => {
    if (!isUserMenuOpen || isMobile) return undefined;
    const handle = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setIsUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isUserMenuOpen, isMobile]);

  // Cerrar drawer si se pasa a desktop
  useEffect(() => {
    if (!isMobile) {
      if (isDrawerControlled) onNavDrawerOpenChange?.(false);
      else setInternalDrawerOpen(false);
    }
  }, [isMobile, isDrawerControlled, onNavDrawerOpenChange]);

  useEffect(() => {
    if (!onUserMenuOpenChange) return undefined;
    onUserMenuOpenChange(isMobile && isUserMenuOpen);
    return undefined;
  }, [isMobile, isUserMenuOpen, onUserMenuOpenChange]);

  const handleLogout = () => { setIsUserMenuOpen(false); onLogoutClick(); };
  const handleOpenAdmin = isStaff
    ? () => { setIsUserMenuOpen(false); navigate('/admin'); }
    : undefined;

  const navProps = {
    onToggleMyLots, isMyLotsOpen,
    onToggleLists, isListsOpen,
    onToggleHistory, isHistoryOpen,
    onToggleLeads, isLeadsOpen,
    onToggleMetaAds, isMetaAdsOpen,
  };

  const showSearchFilters = showSearchControl || showFilterControl;
  // Props del panel por dominio conceptual (contrato en parcelFilterContract).
  const searchFilterProps = {
    isLoggedIn,
    onFlyTo,
    showSearchControl,
    showFilterControl,
    showListingFilters,
    showOfficialUnitFilters,
    showOfficialUnitCoverageNote,
    officialUnitFilter,
    officialUnitZoomLocked,
    lotViewQuotaExceeded,
    lotViewQuotaResetsAt,
    activeFilters,
    activeFilterCount,
    showOnlyFiltered,
    onSetFilterValue,
    onRemoveFilterChip,
    onToggleShowOnlyFiltered,
    onClearAllFilters,
    onOpenFilterResultsList,
    isFilterResultsOpen,
    filterScope,
    filterScopeLists,
    filterScopeListsLoading,
    filterScopeLoading,
    filterScopeError,
    filterScopeCount,
    filterScopeMatchCount,
    onSetFilterScope,
    onFilterScopeDropdownOpen,
    filterScopeLocked,
    filterScopeLockedHint,
    mapFilterOptions,
    onRequestMapFilterOptions,
    parseSearchEnabled,
    parseSearchContext,
    onApplyParsedLotFilters,
    onLotViewQuotaInfo,
    resolveViewProvince,
    filterGroups,
    resolveAppliedChips,
    hideFilterTabs,
    hideShowOnlyFiltered,
    skipPlaceSearch,
  };

  // ─── MOBILE ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <header style={mobileHeaderStyle}>
          {/* Izquierda: hamburguesa + logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {isLoggedIn && (
              <button
                style={hamburgerButtonStyle}
                onClick={() => setIsDrawerOpen((v) => !v)}
                aria-label={isDrawerOpen ? 'Cerrar menú' : 'Abrir menú'}
                aria-expanded={isDrawerOpen}
              >
                {isDrawerOpen
                  ? <CloseIcon style={{ fontSize: '22px' }} />
                  : <MenuIcon style={{ fontSize: '22px' }} />
                }
              </button>
            )}
            {!isLoggedIn && (
            <img
              src={logoTerrioBlanco}
              alt="Terrio"
              style={{ height: '28px', width: 'auto', display: 'block' }}
            />
          )}
          </div>

          {isLoggedIn && (
            <img
              src={logoTerrioBlanco}
              alt="Terrio"
              style={{ height: '28px', width: 'auto', display: 'block' }}
            />
          )}
          
          {/* Derecha: chrome espacio + avatar o login */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            {isLoggedIn ? (
              <SpaceHeaderControl
                compact
                label={headerSpaceLabel}
                mySpaceLabel={mySpaceHeaderLabel}
                isViewingSharedSpace={isViewingSharedSpace}
                sharedSpaces={sharedSpaces}
                activeOwnerUserId={activeOwnerUserId}
                loadingSharedSpaces={loadingSharedSpaces}
                onOpenConfig={() => setSpaceMembersOpen(true)}
                onEnterSpace={enterSpace}
                onExitToOwnSpace={exitToOwnSpace}
                onMenuOpen={() => { refreshSharedSpaces(); }}
              />
            ) : null}
            {isLoggedIn ? (
              <button
                onClick={() => {
                  toast.clear();
                  setIsUserMenuOpen(true);
                }}
                style={{
                  ...authButtonStyle,
                  marginRight: 0,
                  ...(isViewingSharedSpace ? {
                    border: `1px solid ${SPACE_ACCENT_BORDER}`,
                    boxShadow: `0 0 0 2px ${SPACE_ACCENT_RING}`,
                  } : {}),
                }}
                aria-label="Mi cuenta"
              >
                <span style={avatarStyle}>{initial}</span>
              </button>
            ) : !isAuthPage ? (
              <MapHeaderLoginButton
                onClick={onLoginClick}
                label="Ingresar"
                style={{ fontSize: '12px', padding: '0 12px' }}
                iconStyle={{ fontSize: 16, width: 16, height: 16 }}
              />
            ) : null}
          </div>
        </header>

        {showSearchFilters && (
          <MapHeaderSearchFilters {...searchFilterProps} isMobile />
        )}

        {isLoggedIn && (
          <MobileDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            navProps={navProps}
            sharedSpaceLabel={isViewingSharedSpace ? activeSpaceLabel : null}
            onExitSharedSpace={isViewingSharedSpace ? exitToOwnSpace : null}
            hideHistory={isViewingSharedSpace}
          />
        )}

        <UserBottomSheet
          isOpen={isUserMenuOpen}
          onClose={() => setIsUserMenuOpen(false)}
          displayName={displayName}
          userEmail={userEmail}
          initial={initial}
          onOpenBranding={onOpenBranding}
          onOpenFeedback={onOpenFeedback}
          onRestartTour={onRestartTour}
          onLogout={handleLogout}
          onOpenAdmin={handleOpenAdmin}
        />
        <SpaceMembersDialog
          open={spaceMembersOpen}
          onClose={() => setSpaceMembersOpen(false)}
          onSpaceProfileSaved={(profile) => { applyMySpaceProfile(profile); refreshSharedSpaces(); }}
        />
      </>
    );
  }

  // ─── DESKTOP ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes accountMenuIn {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <header style={headerStyle}>
        <div style={headerLogoCellStyle}>
          <img
            src={logoTerrioBlanco}
            alt="Terrio"
            style={{ height: '35px', width: 'auto', display: 'block', marginLeft: '4px', flexShrink: 0 }}
          />
        </div>

        {showSearchFilters && (
          <MapHeaderSearchFilters {...searchFilterProps} />
        )}

        <div style={{ ...iconGroupStyle, ...headerAuthCellStyle }}>
          {isLoggedIn ? (
            <SpaceHeaderControl
              label={headerSpaceLabel}
              mySpaceLabel={mySpaceHeaderLabel}
              isViewingSharedSpace={isViewingSharedSpace}
              sharedSpaces={sharedSpaces}
              activeOwnerUserId={activeOwnerUserId}
              loadingSharedSpaces={loadingSharedSpaces}
              onOpenConfig={() => setSpaceMembersOpen(true)}
              onEnterSpace={enterSpace}
              onExitToOwnSpace={exitToOwnSpace}
              onMenuOpen={() => { refreshSharedSpaces(); }}
            />
          ) : null}
          {isLoggedIn ? (
            <div ref={menuRef} style={{ position: 'relative' }}>
              <Tooltip title="Mi cuenta" placement="bottom" arrow>
                <button onClick={() => setIsUserMenuOpen((v) => {
                  if (!v) toast.clear();
                  return !v;
                })} style={{
                  ...authButtonStyle,
                  ...(isViewingSharedSpace ? {
                    border: `1px solid ${SPACE_ACCENT_BORDER}`,
                    boxShadow: `0 0 0 2px ${SPACE_ACCENT_RING}`,
                  } : {}),
                }}>
                  <span style={avatarStyle}>{initial}</span>
                </button>
              </Tooltip>

              {isUserMenuOpen && (
                <div style={menuStyle}>
                  <AccountMenuPanel
                    displayName={displayName}
                    userEmail={userEmail}
                    initial={initial}
                    onOpenBranding={onOpenBranding}
                    onOpenFeedback={onOpenFeedback}
                    onRestartTour={onRestartTour}
                    onLogout={handleLogout}
                    onOpenAdmin={handleOpenAdmin}
                    onClose={() => setIsUserMenuOpen(false)}
                  />
                </div>
              )}
            </div>
          ) : !isAuthPage ? (
            <MapHeaderLoginButton onClick={onLoginClick} />
          ) : null}
        </div>
      </header>

      {isLoggedIn && (
        <aside
          style={sidebarRailStyle}
          aria-label="Menú principal"
        >
          <RailButton
            isActive={isMyLotsOpen}
            onClick={onToggleMyLots}
            dataTour="mis-lotes"
            tooltip={isMyLotsOpen ? 'Ocultar registros' : 'Registros'}
          >
            <MyLotsIcon style={railIconSvgStyle} />
          </RailButton>

          <RailButton
            isActive={isListsOpen}
            onClick={onToggleLists}
            dataTour="mis-listas"
            tooltip={isListsOpen ? 'Ocultar listas' : 'Listas'}
          >
            <ListAltOutlinedIcon style={railIconSvgStyle} />
          </RailButton>

          {onToggleLeads && (
            <RailButton
              isActive={isLeadsOpen}
              onClick={onToggleLeads}
              dataTour="leads"
              tooltip={isLeadsOpen ? 'Ocultar leads' : 'Leads'}
            >
              <PersonAddOutlinedIcon style={railIconSvgStyle} />
            </RailButton>
          )}

          {/* Historial es personal: no se muestra en espacio ajeno. */}
          {!isViewingSharedSpace && (
            <RailButton isActive={isHistoryOpen} onClick={onToggleHistory}
              disabled={!onToggleHistory} tooltip={isHistoryOpen ? 'Ocultar historial' : 'Historial'} dataTour="historial">
              <HistoryOutlinedIcon style={railIconSvgStyle} />
            </RailButton>
          )}

          {onToggleMetaAds && (
            <RailButton
              isActive={isMetaAdsOpen}
              onClick={onToggleMetaAds}
              dataTour="meta-ads"
              tooltip={isMetaAdsOpen ? 'Ocultar campañas' : 'Campañas'}
            >
              <CampaignOutlinedIcon style={railIconSvgStyle} />
            </RailButton>
          )}

          {/* El pie del rail lo ocupa el botón de feedback (MapControls). */}
          <div style={{ flex: 1 }} />
        </aside>
      )}
      <SpaceMembersDialog
        open={spaceMembersOpen}
        onClose={() => setSpaceMembersOpen(false)}
        onSpaceProfileSaved={(profile) => { applyMySpaceProfile(profile); refreshSharedSpaces(); }}
      />
    </>
  );
}

export default MapHeader;